import React from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import Helpers from "../../../utils/helpers";

export interface TransactionSortOption {
    label: string;
    sortField: string;
    sortOrder: 1 | -1;
}

export interface TransactionDialogProps {
    // Dialog visibility and close handler
    isInline?: boolean;
    visible: boolean;
    onHide: () => void;
    dialogWidth?: string;
    dialogClassName?: string;

    // Header
    userName: string;

    // Transaction data
    transactions: any[];
    isLoading: boolean;
    totalRecords: number;

    // Pagination
    first: number;
    rows: number;
    onPageChange: (event: PaginatorPageChangeEvent) => void;
    rowsPerPageOptions?: number[];

    // Search
    searchInput: string;
    searchQuery: string | null;
    onSearchInputChange: (value: string) => void;
    onSearch: () => void;
    onSearchClear: () => void;

    // Sort
    sortOptions: TransactionSortOption[];
    selectedSortOption: TransactionSortOption | undefined;
    onSortChange: (option: TransactionSortOption) => void;

    // Formatters
    formatNumber: (num: number) => string;
    formatDate: (dateStr: string) => string;
    formatDateTime: (dateString: string) => string;
}

const TransactionDialog: React.FC<TransactionDialogProps> = ({
    isInline = false,
    visible,
    onHide,
    dialogWidth = "900px",
    dialogClassName = "rewardsTransactionDialog",
    userName,
    transactions,
    isLoading,
    totalRecords,
    first,
    rows,
    onPageChange,
    rowsPerPageOptions = [5, 10, 25, 50],
    searchInput,
    searchQuery,
    onSearchInputChange,
    onSearch,
    onSearchClear,
    sortOptions,
    selectedSortOption,
    onSortChange,
    formatNumber,
    formatDate,
    formatDateTime,
}) => {
    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            onSearch();
        }
    };

    const renderContent = () => (
        <div className="transaction-container">
            <div
                className="txSearchBar"
                style={{
                    display: "flex",
                    gap: "1rem",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1.5rem",
                }}
            >
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <span className="p-input-icon-left txSearchInputWrapper">
                        <i className="pi pi-search" />
                        <InputText
                            value={searchInput}
                            onChange={(e) => onSearchInputChange(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            placeholder="Search Through Order #"
                            className="txSearchInput"
                        />
                    </span>
                    <Button
                        icon="pi pi-search"
                        label="Search"
                        className="p-button txSearchBtn"
                        onClick={onSearch}
                    />
                    {searchQuery && (
                        <Button
                            icon="pi pi-times"
                            label="Clear"
                            className="p-button p-button-outlined p-button-secondary txClearBtn"
                            onClick={onSearchClear}
                        />
                    )}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span style={{ fontSize: "0.9rem", color: "#4b5563", fontWeight: 500 }}>
                        Sort By:
                    </span>
                    <Dropdown
                        options={sortOptions}
                        value={selectedSortOption}
                        onChange={(e) => onSortChange(e.value)}
                        optionLabel="label"
                        className="rewardsSortDropdown"
                        disabled={isLoading}
                    />
                </div>
            </div>

            {searchQuery && (
                <div className="txActiveFilterWrapper" style={{ marginBottom: "1rem" }}>
                    <span className="txActiveFilter">
                        <i className="pi pi-filter-fill" />
                        Filtered by: <strong>{searchQuery}</strong>
                    </span>
                </div>
            )}

            {isLoading && transactions.length === 0 ? (
                <div className="rewardsDialogLoading">
                    <ProgressSpinner
                        style={{ width: "40px", height: "40px" }}
                        strokeWidth="4"
                    />
                </div>
            ) : (
                <>
                    {transactions.length === 0 ? (
                        <div
                            className="no-transactions"
                            style={{
                                textAlign: "center",
                                padding: "3rem",
                                color: "#6b7280",
                            }}
                        >
                            <i
                                className="pi pi-inbox"
                                style={{ fontSize: "2rem", marginBottom: "1rem" }}
                            ></i>
                            <p>
                                {searchQuery
                                    ? `No transactions found for "${searchQuery}".`
                                    : "No transactions found."}
                            </p>
                        </div>
                    ) : (
                        <div className="transaction-logs">
                            {transactions.map((transaction) => {
                                const isCredit = transaction.isCredit;

                                return (
                                    <div
                                        key={transaction.id}
                                        className="transaction-log-item"
                                        style={{
                                            padding: "1rem",
                                            borderBottom: "1px solid #e5e7eb",
                                            borderRadius: "0.75rem",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            backgroundColor: "#f9fafb",
                                            marginBottom: "0.75rem",
                                        }}
                                    >
                                        <div
                                            className="transaction-main"
                                            style={{
                                                display: "flex",
                                                gap: "1rem",
                                                alignItems: "flex-start",
                                            }}
                                        >
                                            <div
                                                className="transaction-icon"
                                                style={{
                                                    width: "40px",
                                                    height: "40px",
                                                    borderRadius: "50%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    backgroundColor: isCredit ? "#dcfce7" : "#fee2e2",
                                                    color: isCredit ? "#16a34a" : "#dc2626",
                                                }}
                                            >
                                                <i
                                                    className={`pi ${isCredit
                                                        ? "pi-arrow-down-left"
                                                        : "pi-arrow-up-right"
                                                        }`}
                                                    style={{ fontSize: "1.2rem" }}
                                                ></i>
                                            </div>

                                            <div className="transaction-details">
                                                <div
                                                    className="transaction-title"
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "0.5rem",
                                                        marginBottom: "0.25rem",
                                                    }}
                                                >
                                                    <span
                                                        className="transaction-type"
                                                        style={{
                                                            fontWeight: 600,
                                                            color: "#111827",
                                                        }}
                                                    >
                                                        {transaction.transactionTypeName}
                                                    </span>
                                                    {transaction.status &&
                                                        transaction.transactionTypeName ===
                                                        "Referral Order Earning" && (
                                                            <Tag
                                                                severity={
                                                                    transaction.status === "Active"
                                                                        ? "success"
                                                                        : transaction.status === "Expired"
                                                                            ? "danger"
                                                                            : "warning"
                                                                }
                                                                value={transaction.status}
                                                                style={{
                                                                    fontSize: "0.7rem",
                                                                    padding: "2px 6px",
                                                                }}
                                                            />
                                                        )}
                                                </div>

                                                <div
                                                    className="transaction-meta"
                                                    style={{
                                                        fontSize: "0.85rem",
                                                        color: "#6b7280",
                                                        display: "flex",
                                                        gap: "1rem",
                                                        flexWrap: "wrap",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <span className="transaction-date">
                                                        <i
                                                            className="pi pi-calendar"
                                                            style={{
                                                                fontSize: "0.8rem",
                                                                marginRight: "4px",
                                                            }}
                                                        ></i>
                                                        {formatDateTime(transaction.transactionDate)}
                                                    </span>
                                                    {transaction.orderID && (!isInline || transaction.transactionTypeName !== "Referral Order Earning") && (
                                                        <span className="transaction-order">
                                                            <i
                                                                className="pi pi-tag"
                                                                style={{
                                                                    fontSize: "0.8rem",
                                                                    marginRight: "4px",
                                                                }}
                                                            ></i>
                                                            Order #{transaction.orderID}
                                                        </span>
                                                    )}
                                                    {transaction.referredUserName && (
                                                        <span className="transaction-referral">
                                                            <i
                                                                className="pi pi-user"
                                                                style={{
                                                                    fontSize: "0.8rem",
                                                                    marginRight: "4px",
                                                                    color: "#7295d1ff",
                                                                }}
                                                            ></i>
                                                            {transaction.referredUserName}
                                                        </span>
                                                    )}
                                                    {transaction.expiryDate && (
                                                        <span
                                                            className="transaction-expiry"
                                                            style={{
                                                                color: transaction.isExpired
                                                                    ? "#dc2626"
                                                                    : undefined,
                                                            }}
                                                        >
                                                            <i
                                                                className="pi pi-clock"
                                                                style={{
                                                                    fontSize: "0.8rem",
                                                                    marginRight: "4px",
                                                                }}
                                                            ></i>
                                                            {transaction.isExpired
                                                                ? "Expired: "
                                                                : "Expires: "}
                                                            {formatDate(transaction.expiryDate)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            className="transaction-points"
                                            style={{
                                                textAlign: "right",
                                                minWidth: "120px",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "2px",
                                                }}
                                            >
                                                {transaction.rewardToCurrencyRatio > 0 ? (
                                                    <>
                                                        <span
                                                            className="points"
                                                            style={{
                                                                display: "block",
                                                                fontWeight: "bold",
                                                                fontSize: "1.1rem",
                                                                color: isCredit ? "#16a34a" : "#dc2626",
                                                            }}
                                                        >
                                                            {Helpers.formatAmountInINR(
                                                                Math.abs(Math.round(
                                                                    transaction.points *
                                                                    transaction.rewardToCurrencyRatio
                                                                ))
                                                            )}
                                                        </span>
                                                        <span
                                                            style={{
                                                                fontSize: "0.85rem",
                                                                color: "#6b7280",
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {isCredit ? "+" : ""}
                                                            {formatNumber(transaction.points)} pts
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span
                                                        className="points"
                                                        style={{
                                                            display: "block",
                                                            fontWeight: "bold",
                                                            fontSize: "1.1rem",
                                                            color: isCredit ? "#16a34a" : "#dc2626",
                                                        }}
                                                    >
                                                        {isCredit ? "+" : ""}
                                                        {formatNumber(transaction.points)} pts
                                                    </span>
                                                )}
                                            </div>
                                            {isCredit &&
                                                transaction.transactionTypeName !==
                                                "Redemption Reversal" && (
                                                    <span
                                                        className="remaining"
                                                        style={{
                                                            fontSize: "0.8rem",
                                                            color: "#6b7280",
                                                            display: "block",
                                                            marginTop: "4px",
                                                        }}
                                                    >
                                                        {formatNumber(transaction.remainingPoints)} left
                                                    </span>
                                                )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {totalRecords > 0 && (
                        <div
                            style={{
                                marginTop: "1rem",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "1rem",
                            }}
                        >
                            <Paginator
                                first={first}
                                rows={rows}
                                totalRecords={totalRecords}
                                rowsPerPageOptions={rowsPerPageOptions}
                                onPageChange={onPageChange}
                            />
                            <span style={{ fontSize: "0.88rem", color: "#374151" }}>
                                Total: <strong>{totalRecords}</strong>
                            </span>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    // 2. Conditional Rendering
    if (isInline) {
        return renderContent();
    }

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={`Transactions - ${userName}`}
            modal
            style={{ width: dialogWidth, borderRadius: "0.75rem" }}
            headerStyle={{ borderRadius: "0.75rem 0.75rem 0 0" }}
        >
            {renderContent()}
        </Dialog>
    );
};

export default TransactionDialog;
